'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type ChatExpansionStore = {
  read: (expansionKey: string) => boolean | undefined;
  write: (expansionKey: string, isExpanded: boolean) => void;
};

export const ChatExpansionContext = createContext<ChatExpansionStore | null>(
  null,
);

export function useChatExpansionState({
  expansionKey,
  defaultExpanded,
  forceExpanded,
}: {
  expansionKey: string | undefined;
  defaultExpanded: boolean;
  forceExpanded?: boolean;
}): [boolean, (isExpanded: boolean) => void] {
  const store = useContext(ChatExpansionContext);
  const [isExpanded, setIsExpanded] = useState(() => {
    if (!expansionKey || !store) {
      return defaultExpanded;
    }

    return store.read(expansionKey) ?? defaultExpanded;
  });

  const wasForcedRef = useRef(forceExpanded === true);

  useEffect(() => {
    const hasUserChoice =
      expansionKey !== undefined && store?.read(expansionKey) !== undefined;
    const becameForced = forceExpanded === true && !wasForcedRef.current;

    wasForcedRef.current = forceExpanded === true;

    if (
      becameForced ||
      ((forceExpanded || defaultExpanded) && !hasUserChoice)
    ) {
      setIsExpanded(true);
    }
  }, [defaultExpanded, expansionKey, forceExpanded, store]);

  const setExpandedByUser = useCallback(
    (nextIsExpanded: boolean) => {
      setIsExpanded(nextIsExpanded);

      if (expansionKey) {
        store?.write(expansionKey, nextIsExpanded);
      }
    },
    [expansionKey, store],
  );

  return [isExpanded, setExpandedByUser];
}
